import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Settings, AlertCircle, Play, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ReAnalysisEngineProps {
  selectedBotId?: string;
}

interface ReAnalysisStatus {
  isRunning: boolean;
  progress: number;
  status: string;
  error?: string;
  result?: any;
}

export const ReAnalysisEngine: React.FC<ReAnalysisEngineProps> = ({
  selectedBotId
}) => {
  const [analysisStatus, setAnalysisStatus] = useState<ReAnalysisStatus>({
    isRunning: false,
    progress: 0,
    status: 'ready'
  });
  const { toast } = useToast();

  // Check re-analysis status when bot is selected
  useEffect(() => {
    const checkBotStatus = async () => {
      if (!selectedBotId) {
        setAnalysisStatus({
          isRunning: false,
          progress: 0,
          status: 'ready'
        });
        return;
      }

      try {
        const { ReAnalysisService } = await import('@/services/ReAnalysisService');
        const statusCheck = await ReAnalysisService.checkReAnalysisStatus(selectedBotId);
        
        if (statusCheck.success && statusCheck.completed) {
          setAnalysisStatus({
            isRunning: false,
            progress: 100,
            status: 'blocked',
            error: `Re-analysis completed on ${new Date(statusCheck.completedAt!).toLocaleDateString()}. Run Pattern Analysis first to reset.`
          });
        } else {
          setAnalysisStatus({
            isRunning: false,
            progress: 0,
            status: 'ready'
          });
        }
      } catch (error) {
        console.error('Error checking bot status:', error);
        setAnalysisStatus({
          isRunning: false,
          progress: 0,
          status: 'ready'
        });
      }
    };

    checkBotStatus();
  }, [selectedBotId]);

  const handleStartReAnalysis = async () => {
    if (!selectedBotId) {
      toast({
        title: "No Bot Selected",
        description: "Please select a bot first to run re-analysis",
        variant: "destructive"
      });
      return;
    }

    try {
      setAnalysisStatus({
        isRunning: true,
        progress: 5,
        status: 'Checking re-analysis status...'
      });

      // First check if re-analysis is already completed
      const { ReAnalysisService } = await import('@/services/ReAnalysisService');
      const statusCheck = await ReAnalysisService.checkReAnalysisStatus(selectedBotId);
      
      if (statusCheck.success && statusCheck.completed) {
        setAnalysisStatus({
          isRunning: false,
          progress: 0,
          status: 'blocked',
          error: `Re-analysis already completed on ${new Date(statusCheck.completedAt!).toLocaleDateString()}. Please run Pattern Analysis first to unlock re-analysis.`
        });
        
        toast({
          title: "Re-Analysis Already Completed",
          description: "Please run Pattern Analysis first to unlock re-analysis for this bot",
          variant: "destructive"
        });
        return;
      }

      setAnalysisStatus(prev => ({
        ...prev,
        progress: 10,
        status: 'Initializing re-analysis...'
      }));

      // Get bot details and linked trading file ID
      const { data: botData, error: botError } = await supabase
        .from('bots')
        .select('id, owner_id, linked_trading_file_id')
        .eq('id', selectedBotId)
        .single();

      if (botError || !botData?.linked_trading_file_id) {
        throw new Error('Bot not found or no trading file associated');
      }

      setAnalysisStatus(prev => ({
        ...prev,
        progress: 30,
        status: 'Fetching bot configuration...'
      }));

      // Call the NEW honest re-analysis edge function
      const { data, error } = await supabase.functions.invoke('re-analysis-honest-engine', {
        body: {
          botId: selectedBotId,
          tradingFileId: botData.linked_trading_file_id,
          config: {
            minLength: 3,
            maxLength: 7,
            minFrequency: 2,
            mode: 'progressive'
          }
        }
      });

      if (error) {
        throw new Error(error.message || 'Re-analysis failed');
      }

      // Update re-analysis status
      await ReAnalysisService.updateReAnalysisStatus(selectedBotId);

      setAnalysisStatus({
        isRunning: false,
        progress: 100,
        status: 'completed',
        result: data
      });

      toast({
        title: "Re-Analysis Complete",
        description: `Successfully processed ${data.data?.totalCombinedTrades || 0} trades and updated ${data.data?.patternsProcessed || 0} patterns`,
      });

    } catch (error) {
      console.error('Re-analysis error:', error);
      
      setAnalysisStatus({
        isRunning: false,
        progress: 0,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      toast({
        title: "Re-Analysis Failed",
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: "destructive"
      });
    }
  };

  const getStatusIcon = () => {
    if (analysisStatus.isRunning) {
      return <RefreshCw className="h-4 w-4 animate-spin" />;
    }
    if (analysisStatus.status === 'completed') {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    if (analysisStatus.status === 'error') {
      return <XCircle className="h-4 w-4 text-red-500" />;
    }
    return <Play className="h-4 w-4" />;
  };

  const getStatusColor = () => {
    if (analysisStatus.status === 'completed') return 'text-green-600';
    if (analysisStatus.status === 'error') return 'text-red-600';
    if (analysisStatus.isRunning) return 'text-blue-600';
    return 'text-gray-600';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Re-Analysis Engine
          <Badge variant="secondary" className="ml-2">
            Production Ready
          </Badge>
        </CardTitle>
        <CardDescription>
          Progressive pattern re-analysis using Honest Backtest logic with real-time data integration
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
          <AlertCircle className="h-5 w-5 text-blue-500" />
          <div className="flex-1">
            <p className="text-sm font-medium">Re-Analysis Engine</p>
            <p className="text-xs text-muted-foreground">
              Safely merges CSV data + real-time trades using progressive learning (optimized for speed)
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-medium">Optimized Speed</span>
            </div>
            <p className="text-xs text-muted-foreground">
              60% faster processing (4-6s vs 12-14s)
            </p>
          </div>
          
          <div className="p-3 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium">Progressive Update</span>
            </div>
            <p className="text-xs text-muted-foreground">
              UPSERT patterns using Honest Backtest logic
            </p>
          </div>
          
          <div className="p-3 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-medium">Real-time Sync</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Auto-update patterns with new EXIT trades
            </p>
          </div>
        </div>

        {/* Progress Section */}
        {analysisStatus.isRunning && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Progress</span>
              <span className="text-sm text-muted-foreground">{analysisStatus.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${analysisStatus.progress}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground">{analysisStatus.status}</p>
          </div>
        )}

        {/* Status Message */}
        {!analysisStatus.isRunning && analysisStatus.status !== 'ready' && (
          <div className={`p-3 border rounded-lg ${analysisStatus.status === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <span className={`text-sm font-medium ${getStatusColor()}`}>
                {analysisStatus.status === 'completed' ? 'Re-Analysis Completed Successfully' : 'Re-Analysis Failed'}
              </span>
            </div>
            {analysisStatus.error && (
              <p className="text-xs text-red-600 mt-1">{analysisStatus.error}</p>
            )}
            {analysisStatus.result && (
              <div className="text-xs text-green-600 mt-1">
                <p>CSV Trades: {analysisStatus.result.data?.csvTradesCount || 0}</p>
                <p>Real-time Trades: {analysisStatus.result.data?.realtimeTradesCount || 0}</p>
                <p>Patterns Updated: {analysisStatus.result.data?.patternsProcessed || 0}</p>
              </div>
            )}
          </div>
        )}

        <Button 
          onClick={handleStartReAnalysis}
          disabled={analysisStatus.isRunning || !selectedBotId || analysisStatus.status === 'blocked'}
          className="w-full"
        >
          {getStatusIcon()}
          <span className="ml-2">
            {analysisStatus.isRunning 
              ? 'Running Re-Analysis...' 
              : analysisStatus.status === 'blocked'
                ? 'Re-Analysis Completed'
              : selectedBotId 
                ? 'Start Re-Analysis Engine'
                : 'Select a Bot to Start'
            }
          </span>
        </Button>

        {!selectedBotId && (
          <p className="text-xs text-muted-foreground text-center">
            Please select a bot from the Bot-CSV Linking tab above to enable re-analysis
          </p>
        )}
      </CardContent>
    </Card>
  );
};